const AUTO_GIFT_STATE_KEY = 'melume:auto-gift:state';

if (!customElements.get('auto-gift-manager')) {
  class GiftCheckoutGuard {
    static updateControls() {
      const isBusy = this.isChecking || this.isNavigating || this.mutations.size > 0 || this.reconciliations > 0;
      document.querySelectorAll('[data-gift-checkout]').forEach((buttonNode) => {
        buttonNode.setAttribute('aria-disabled', String(isBusy));
        buttonNode.setAttribute('aria-busy', String(isBusy));
      });
    }

    static async waitForCart() {
      while (this.mutations.size > 0 || this.reconciliations > 0) {
        await Promise.allSettled([...this.mutations, AutoGiftManager.pending]);
      }
    }

    static async validateCart() {
      await this.waitForCart();
      if (this.isNavigating) {
        throw new Error('Cart navigation is still pending.');
      }
      for (let attempt = 0; attempt < 3; attempt += 1) {
        const managerNode = AutoGiftManager.getManager();
        if (!managerNode) {
          throw new Error('Gift manager is unavailable.');
        }
        await managerNode.reconcile(undefined, true);
        await this.waitForCart();
        const revision = this.revision;
        const cart = await managerNode.requestCart('cart.js');
        const plan = managerNode.createSelectionPlan(cart, JSON.parse(managerNode.dataset.selectionConfig));
        if (managerNode.dataset.autoEnabled === 'true') {
          managerNode.addAutomaticGiftsToPlan(cart, plan, managerNode.loadState(cart.token));
        }
        if (!this.isNavigating && revision === this.revision && this.mutations.size === 0 && this.reconciliations === 0 && Object.keys(plan.updates).length === 0 && plan.additions.length === 0 && plan.changes.length === 0) {
          if (cart.items.length === 0) {
            throw new Error('The cart is empty.');
          }
          return;
        }
        await this.waitForCart();
      }
      throw new Error('The cart is still changing.');
    }

    static prepareCheckout() {
      if (this.checkoutOperation) {
        return this.checkoutOperation;
      }
      this.isChecking = true;
      this.updateControls();
      this.checkoutOperation = this.validateCart().finally(() => {
        this.isChecking = false;
        this.checkoutOperation = null;
        this.updateControls();
      });
      return this.checkoutOperation;
    }
  }

  GiftCheckoutGuard.mutations = new Set();
  GiftCheckoutGuard.reconciliations = 0;
  GiftCheckoutGuard.revision = 0;
  const originalFetch = window.fetch.bind(window);
  window.fetch = (resource, options) => {
    const requestUrl = new URL(typeof resource === 'string' || resource instanceof URL ? resource : resource.url, window.location.origin);
    const method = (options?.method || resource.method || 'GET').toUpperCase();
    if (requestUrl.origin !== window.location.origin || method !== 'POST' || !/\/cart\/(add|change|update|clear)(\.js)?$/.test(requestUrl.pathname)) {
      return originalFetch(resource, options);
    }
    const operation = originalFetch(resource, options).then(async (response) => {
      await response.clone().arrayBuffer();
      return response;
    });
    GiftCheckoutGuard.mutations.add(operation);
    GiftCheckoutGuard.revision += 1;
    GiftCheckoutGuard.updateControls();
    const completeMutation = () => {
      GiftCheckoutGuard.mutations.delete(operation);
      GiftCheckoutGuard.updateControls();
    };
    operation.then(completeMutation, completeMutation);
    return operation;
  };

  document.documentElement.addEventListener('gift:checkout', (checkoutEvent) => {
    GiftCheckoutGuard.prepareCheckout().then(checkoutEvent.detail.resolve, checkoutEvent.detail.reject);
  });
  document.addEventListener('click', (cartEvent) => {
    const cartLinkNode = cartEvent.target.closest('line-item-quantity a');
    if (cartLinkNode && window.themeVariables?.settings.pageType === 'cart' && !cartEvent.ctrlKey && !cartEvent.metaKey && !cartEvent.shiftKey && !cartEvent.altKey && cartEvent.button === 0) {
      GiftCheckoutGuard.isNavigating = true;
      GiftCheckoutGuard.updateControls();
    }
  }, { capture: true });
  document.addEventListener('change', (cartEvent) => {
    if (cartEvent.target.matches('line-item-quantity input') && window.themeVariables?.settings.pageType === 'cart') {
      GiftCheckoutGuard.isNavigating = true;
      GiftCheckoutGuard.updateControls();
    }
  }, { capture: true });
  const approvedCheckoutForms = new WeakSet();
  const handleNativeCheckout = async (checkoutEvent) => {
    const formNode = checkoutEvent.type === 'submit' ? checkoutEvent.target : null;
    const linkNode = checkoutEvent.type === 'click' ? checkoutEvent.target.closest('a[href]') : null;
    const isCheckoutForm = formNode?.matches('form[action="/checkout"], form[action$="/cart"]') && (checkoutEvent.submitter?.name === 'checkout' || formNode.querySelector('[name="checkout"]'));
    const isCheckoutLink = linkNode && /\/checkout(?:[/?#]|$)/.test(new URL(linkNode.href).pathname);
    if ((!isCheckoutForm && !isCheckoutLink) || (formNode || linkNode).hasAttribute('chekly-procceded')) {
      return;
    }
    if (formNode && approvedCheckoutForms.delete(formNode)) {
      return;
    }
    checkoutEvent.preventDefault();
    checkoutEvent.stopImmediatePropagation();
    if (GiftCheckoutGuard.isNativeCheckoutPending) {
      return;
    }
    GiftCheckoutGuard.isNativeCheckoutPending = true;
    let timeoutId;
    try {
      await Promise.race([
        GiftCheckoutGuard.prepareCheckout(),
        new Promise((resolve, reject) => { timeoutId = setTimeout(() => reject(new Error('Cart validation timed out.')), 20000); })
      ]);
      if (formNode) {
        const currentFormNode = formNode.isConnected ? formNode : document.getElementById(formNode.id) || document.querySelector('form.cart__recap');
        if (!currentFormNode) {
          throw new Error('Checkout form is unavailable.');
        }
        approvedCheckoutForms.add(currentFormNode);
        currentFormNode.requestSubmit(checkoutEvent.submitter?.form === currentFormNode ? checkoutEvent.submitter : undefined);
      } else {
        window.location.assign(linkNode.href);
      }
    } catch (error) {
      console.error('[gift-checkout]', error);
    } finally {
      clearTimeout(timeoutId);
      GiftCheckoutGuard.isNativeCheckoutPending = false;
    }
  };
  window.addEventListener('submit', handleNativeCheckout, { capture: true });
  window.addEventListener('click', handleNativeCheckout, { capture: true });
  window.addEventListener('pageshow', () => {
    GiftCheckoutGuard.isNavigating = false;
    GiftCheckoutGuard.updateControls();
  });
  new MutationObserver(() => GiftCheckoutGuard.updateControls()).observe(document.documentElement, { childList: true, subtree: true });

  class AutoGiftManager extends HTMLElement {
    connectedCallback() {
      queueMicrotask(() => {
        AutoGiftManager.getManager()?.reconcile();
      });
    }

    static getManager() {
      return document.querySelector('auto-gift-manager[data-main-cart-section]') || document.querySelector('auto-gift-manager');
    }

    reconcile(selection, isCheckout = false) {
      GiftCheckoutGuard.reconciliations += 1;
      GiftCheckoutGuard.updateControls();
      const operation = AutoGiftManager.pending.catch(() => {}).then(() => this.reconcileCart(selection, isCheckout)).finally(() => {
        GiftCheckoutGuard.reconciliations -= 1;
        GiftCheckoutGuard.updateControls();
      });
      AutoGiftManager.pending = operation;
      return operation;
    }

    async reconcileCart(selection, isCheckout = false) {
      let itemsChanged = false;
      let cart;
      let operationError;

      try {
        cart = await this.requestCart('cart.js');
        const config = JSON.parse(this.dataset.selectionConfig);
        const plan = this.createSelectionPlan(cart, config, selection);
        const state = this.loadState(cart.token);

        if (this.dataset.autoEnabled === 'true') {
          this.addAutomaticGiftsToPlan(cart, plan, state);
        }

        if (Object.keys(plan.updates).length > 0) {
          cart = await this.requestCart('cart/update.js', { updates: plan.updates });
          itemsChanged = true;
        }

        for (const change of plan.changes) {
          const sampleLine = cart.items.find((lineItem) => lineItem.properties?._gift_selection === 'sample' && String(lineItem.variant_id) === String(change.variant));
          if (!sampleLine) {
            throw new Error('Sample item is no longer available.');
          }
          cart = await this.requestCart('cart/change.js', { id: sampleLine.key, quantity: change.quantity });
          itemsChanged = true;
        }

        if (plan.additions.length > 0) {
          await this.requestCart('cart/add.js', { items: plan.additions });
          itemsChanged = true;
          cart = await this.requestCart('cart.js');
        }

        const thresholdLine = cart.items.find((lineItem) => lineItem.properties?._gift_selection === 'threshold');
        const giftTitle = thresholdLine?.product_title || '';

        if ((cart.attributes?.['Selected Gift'] || '') !== giftTitle) {
          cart = await this.requestCart('cart/update.js', { attributes: { 'Selected Gift': giftTitle } });
          itemsChanged = true;
        }

        this.saveState(state);
        if (plan.selectionError) {
          throw plan.selectionError;
        }
        if (selection && (plan.hasSampleAddition || plan.additions.some((addition) => addition.properties._gift_selection === selection.kind))) {
          const selectedLine = cart.items.find((lineItem) => lineItem.properties?._gift_selection === selection.kind && String(lineItem.variant_id) === String(selection.variant));
          const selectedProduct = (selection.kind === 'sample' ? config.samples : config.gifts).find((product) => String(product.variant) === String(selection.variant));
          const price = (selectedLine.final_price ?? selectedLine.final_line_price / selectedLine.quantity) / 100;
          document.documentElement.dispatchEvent(new CustomEvent('analytics-custom:subscribe', {
            bubbles: true,
            detail: {
              type: 'add_to_cart',
              dataLayerItem: {
                event: 'add_to_cart',
                ecommerce: {
                  currency: cart.currency,
                  value: price,
                  items: [{ item_name: selectedLine.product_title, item_id: selectedProduct.analyticsId || '', price, item_category: selectedLine.product_type || '', quantity: 1 }]
                }
              }
            }
          }));
        }
        document.documentElement.dispatchEvent(new CustomEvent('gift:updated', { bubbles: true, detail: { cart } }));

      } catch (error) {
        operationError = error;
        console.error('[auto-gift]', error);
        try {
          cart = await this.requestCart('cart.js');
          const thresholdLine = cart.items.find((lineItem) => lineItem.properties?._gift_selection === 'threshold');
          const giftTitle = thresholdLine?.product_title || '';
          if ((cart.attributes?.['Selected Gift'] || '') !== giftTitle) {
            cart = await this.requestCart('cart/update.js', { attributes: { 'Selected Gift': giftTitle } });
            itemsChanged = true;
          }
        } catch (refreshError) {}
        if (selection) {
          selection.failed = true;
        }
        if (cart) {
          document.documentElement.dispatchEvent(new CustomEvent('gift:updated', { bubbles: true, detail: { cart } }));
        }
      }

      if (itemsChanged && !isCheckout) {
        await this.refreshCart(selection);
      }
      if (selection) {
        if (operationError) {
          selection.reject(operationError);
        } else {
          selection.resolve();
        }
      }
      if (isCheckout && operationError) {
        throw operationError;
      }
    }

    createSelectionPlan(cart, config, selection) {
      const plan = { updates: {}, additions: [], changes: [] };
      const paidLines = cart.items.filter((lineItem) => !lineItem.properties?._auto_gift && !lineItem.properties?._gift_selection);
      const giftTotal = cart.items.reduce((total, lineItem) => total + (lineItem.properties?._auto_gift || lineItem.properties?._gift_selection ? lineItem.final_line_price : 0), 0);
      const hasPurchase = paidLines.some((lineItem) => lineItem.quantity > 0);
      const isEligible = config.enabled && hasPurchase && cart.total_price - giftTotal >= config.threshold && !paidLines.some((lineItem) => config.excludedProducts.includes(lineItem.product_id));
      const giftVariants = new Set(config.gifts.map((gift) => String(gift.variant)));
      const sampleVariants = new Set(config.samples.map((sample) => String(sample.variant)));
      const sampleGroups = new Map();
      let sampleQuantity = 0;
      let keptThresholdVariant;

      for (const lineItem of cart.items) {
        const kind = lineItem.properties?._gift_selection;
        const variantId = String(lineItem.variant_id);

        if (kind === 'threshold') {
          const matchesChoice = !selection || selection.kind !== 'threshold' || String(selection.variant) === variantId;
          if (isEligible && giftVariants.has(variantId) && matchesChoice && !keptThresholdVariant) {
            keptThresholdVariant = variantId;
            if (lineItem.quantity !== 1) {
              plan.updates[lineItem.key] = 1;
            }
          } else {
            plan.updates[lineItem.key] = 0;
          }
        } else if (kind === 'sample') {
          const sampleProduct = config.samples.find((sample) => String(sample.variant) === variantId);
          const group = sampleGroups.get(variantId) || { quantity: 0, lines: [] };
          const availableQuantity = Math.max(0, Math.min(2 - sampleQuantity, (sampleProduct?.maxQuantity ?? 2) - group.quantity));
          const quantity = hasPurchase && config.samplesEnabled !== false && sampleVariants.has(variantId) ? Math.min(lineItem.quantity, availableQuantity) : 0;
          if (quantity > 0) {
            group.quantity += quantity;
            group.lines.push(lineItem);
            sampleGroups.set(variantId, group);
            sampleQuantity += quantity;
          }
          if (quantity !== lineItem.quantity) {
            plan.updates[lineItem.key] = quantity;
          }
        }
      }

      if (selection?.kind === 'threshold') {
        if (!isEligible || !giftVariants.has(String(selection.variant))) {
          plan.selectionError = new Error('Gift selection is no longer available.');
        } else if (!keptThresholdVariant) {
          plan.additions.push(this.selectionItem(selection.variant, 'threshold'));
        }
      } else if (selection?.kind === 'sample') {
        const variantId = String(selection.variant);
        const group = sampleGroups.get(variantId);
        const sampleProduct = config.samples.find((sample) => String(sample.variant) === variantId);
        const isIncrease = selection.action === 'increase';
        const isDecrease = selection.action === 'decrease';
        const quantity = (group?.quantity || 0) + (isIncrease ? 1 : -1);
        if (!hasPurchase || config.samplesEnabled === false || !sampleProduct || (!isIncrease && !isDecrease) || quantity < 0 || quantity > (sampleProduct.maxQuantity ?? 2) || (isIncrease && sampleQuantity >= 2)) {
          plan.selectionError = new Error('Sample quantity is no longer available.');
        } else if (group) {
          group.lines.slice(1).forEach((lineItem) => { plan.updates[lineItem.key] = 0; });
          delete plan.updates[group.lines[0].key];
          plan.changes.push({ variant: selection.variant, quantity });
          plan.hasSampleAddition = isIncrease;
        } else if (isIncrease) {
          plan.additions.push(this.selectionItem(selection.variant, 'sample'));
        }
      }

      return plan;
    }

    selectionItem(variantId, kind) {
      return { id: Number(variantId), quantity: 1, properties: { _gift_selection: kind } };
    }

    addAutomaticGiftsToPlan(cart, plan, state) {
      const gifts = JSON.parse(this.dataset.gifts || '[]');
      const configuredVariants = new Set(gifts.map((gift) => String(gift.variant)));
      const paidLines = cart.items.filter((lineItem) => !lineItem.properties?._auto_gift && !lineItem.properties?._gift_selection);

      cart.items.filter((lineItem) => lineItem.properties?._auto_gift && !configuredVariants.has(String(lineItem.variant_id))).forEach((lineItem) => {
        plan.updates[lineItem.key] = 0;
      });

      for (const gift of gifts) {
        const variantId = String(gift.variant);
        const giftState = state.gifts[variantId] || {};
        const requiredQuantity = paidLines.reduce((quantity, lineItem) => quantity + (gift.triggers.includes(String(lineItem.product_id)) ? lineItem.quantity : 0), 0);
        const giftLines = cart.items.filter((lineItem) => lineItem.properties?._auto_gift && String(lineItem.variant_id) === variantId);
        const presentQuantity = giftLines.reduce((quantity, lineItem) => quantity + lineItem.quantity, 0);

        if (requiredQuantity === 0) {
          giftLines.forEach((lineItem) => { plan.updates[lineItem.key] = 0; });
          delete state.gifts[variantId];
        } else if (presentQuantity > 0) {
          if (presentQuantity !== requiredQuantity || giftLines.length > 1) {
            giftLines.forEach((lineItem, index) => { plan.updates[lineItem.key] = index === 0 ? requiredQuantity : 0; });
          }
          state.gifts[variantId] = { added: true };
        } else if (typeof giftState.removedAt === 'number') {
          if (requiredQuantity > giftState.removedAt) {
            plan.additions.push({ id: gift.variant, quantity: requiredQuantity, properties: { _auto_gift: 'true' } });
            state.gifts[variantId] = { added: true };
          } else if (requiredQuantity < giftState.removedAt) {
            giftState.removedAt = requiredQuantity;
          }
        } else if (giftState.added) {
          state.gifts[variantId] = { added: true, removedAt: requiredQuantity };
        } else {
          plan.additions.push({ id: gift.variant, quantity: requiredQuantity, properties: { _auto_gift: 'true' } });
          state.gifts[variantId] = { added: true };
        }
      }
    }

    loadState(token) {
      try {
        const state = JSON.parse(localStorage.getItem(AUTO_GIFT_STATE_KEY));
        if (state && state.token === token && state.gifts) {
          return state;
        }
      } catch (error) {}
      return { token, gifts: {} };
    }

    saveState(state) {
      try {
        if (Object.keys(state.gifts).length > 0) {
          localStorage.setItem(AUTO_GIFT_STATE_KEY, JSON.stringify(state));
        } else {
          localStorage.removeItem(AUTO_GIFT_STATE_KEY);
        }
      } catch (error) {}
    }

    async requestCart(endpoint, body) {
      const response = await fetch(`${window.Shopify.routes.root}${endpoint}`, body ? {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body)
      } : { cache: 'no-store' });
      const result = await response.json();
      if (!response.ok) {
        throw new Error(result.description || result.message || 'Cart update failed.');
      }
      return result;
    }

    restoreSelectionFocus(containerNode, selection) {
      const selectorNodes = Array.from(containerNode.querySelectorAll(`gift-selection[data-kind="${selection.kind}"]`));
      const matchingSelectorNodes = selectorNodes.filter((candidateNode) => selection.kind !== 'sample' || candidateNode.dataset.variantId === String(selection.variant) && candidateNode.dataset.source === selection.source);
      const visibleSelectorNodes = matchingSelectorNodes.filter((candidateNode) => candidateNode.getClientRects().length > 0);
      const selectorNode = visibleSelectorNodes[0] || (selection.source === 'cart' ? selectorNodes.find((candidateNode) => candidateNode.dataset.source === 'card' && candidateNode.dataset.variantId === String(selection.variant)) : null);
      if (!selectorNode) {
        if (selection.source === 'drawer') {
          containerNode.querySelector('[data-action="close"]')?.focus();
        }
        return;
      }
      const statusNode = selectorNode?.querySelector('[data-gift-status]');
      if (statusNode) {
        statusNode.textContent = selection.failed ? selectorNode.dataset.errorText : '';
      }
      const announcementNode = selectorNode?.querySelector('[data-gift-announcement]');
      if (announcementNode && !selection.failed) {
        announcementNode.textContent = selectorNode.dataset.successText;
      }
      if (selection.failed) {
        statusNode?.focus();
      } else if (selection.variant && selectorNode) {
        const controls = Array.from(selectorNode.querySelectorAll('[data-gift-choice]')).filter((choiceNode) => !choiceNode.disabled && choiceNode.getClientRects().length > 0);
        const focusNode = controls.find((choiceNode) => choiceNode.dataset.sampleAction === selection.action) || controls[0];
        focusNode?.focus();
      }
    }

    restoreDrawerFocus(selection) {
      const drawerNode = document.querySelector('cart-drawer');
      if (!drawerNode || !drawerNode.hasAttribute('open')) {
        return;
      }
      const previousManagerNode = drawerNode.querySelector('auto-gift-manager');
      const observer = new MutationObserver(() => {
        if (drawerNode.querySelector('auto-gift-manager') === previousManagerNode) {
          return;
        }
        observer.disconnect();
        clearTimeout(timeoutId);
        if (drawerNode.hasAttribute('open')) {
          this.restoreSelectionFocus(drawerNode, selection);
        }
      });
      observer.observe(drawerNode, { childList: true });
      const timeoutId = setTimeout(() => observer.disconnect(), 3000);
    }

    async refreshCart(selection) {
      const sectionId = this.dataset.mainCartSection;

      try {
        if (sectionId) {
          const response = await fetch(`${window.Shopify.routes.root}cart?sections=${encodeURIComponent(sectionId)}`);
          if (!response.ok) {
            throw new Error('Cart section refresh failed.');
          }
          const sections = await response.json();
          const sectionNode = document.getElementById(`shopify-section-${sectionId}`);
          if (!sections[sectionId] || !sectionNode) {
            throw new Error('Cart section is unavailable.');
          }
          const sectionDocument = new DOMParser().parseFromString(sections[sectionId], 'text/html');
          sectionNode.replaceChildren(...sectionDocument.getElementById(`shopify-section-${sectionId}`).childNodes);
          if (selection && selection.source !== 'drawer') {
            this.restoreSelectionFocus(sectionNode, selection);
          }
        }
        const cart = await this.requestCart('cart.js');
        document.documentElement.dispatchEvent(new CustomEvent('cart:updated', { bubbles: true, detail: { cart } }));
        if (selection?.source === 'drawer') {
          this.restoreDrawerFocus(selection);
        }
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true, detail: { cart } }));
      } catch (error) {
        window.location.reload();
      }
    }
  }

  AutoGiftManager.pending = Promise.resolve();
  customElements.define('auto-gift-manager', AutoGiftManager);

  document.documentElement.addEventListener('gift:select', (selectionEvent) => {
    const managerNode = AutoGiftManager.getManager();
    if (managerNode) {
      managerNode.reconcile(selectionEvent.detail);
    } else {
      selectionEvent.detail.reject(new Error('Gift manager is unavailable.'));
    }
  });

  document.addEventListener('click', (quantityEvent) => {
    const quantityLinkNode = quantityEvent.target.closest('[data-quantity-increase]');
    if (!quantityLinkNode) {
      return;
    }
    const { title, id, price, type, nextQuantity, currency } = quantityLinkNode.dataset;
    document.documentElement.dispatchEvent(new CustomEvent('analytics-custom:subscribe', {
      bubbles: true,
      detail: {
        type: 'add_to_cart',
        dataLayerItem: {
          event: 'add_to_cart',
          ecommerce: {
            currency,
            value: price,
            items: [{ item_name: title, item_id: id, price, item_category: type || '', quantity: nextQuantity || 1 }]
          }
        }
      }
    }));
  }, { capture: true });
}

if (!customElements.get('gift-selection')) {
  class GiftSelection extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      const listenerOptions = { signal: this.abortController.signal };
      this.addEventListener(this.dataset.kind === 'sample' ? 'click' : 'change', (selectionEvent) => this.selectGift(selectionEvent), listenerOptions);
      document.documentElement.addEventListener('gift:updated', (updatedEvent) => this.updateSelection(updatedEvent.detail.cart), listenerOptions);
    }

    disconnectedCallback() {
      this.abortController.abort();
    }

    async selectGift(selectionEvent) {
      const controlNode = selectionEvent.target.closest('[data-gift-choice]');
      if (!controlNode || this.isBusy || controlNode.disabled) {
        return;
      }
      this.isBusy = true;
      this.setAttribute('aria-busy', 'true');
      const statusNode = this.querySelector('[data-gift-status]');
      const announcementNode = this.querySelector('[data-gift-announcement]');
      const optionNode = controlNode.closest('[data-gift-option]');
      const loaderNode = optionNode?.querySelector('[data-gift-loader]');
      statusNode.textContent = '';
      if (announcementNode) {
        announcementNode.textContent = '';
      }
      if (loaderNode) {
        loaderNode.hidden = false;
        optionNode.setAttribute('aria-busy', 'true');
      }
      this.querySelectorAll('[data-gift-choice]').forEach((choiceNode) => { choiceNode.disabled = true; });
      try {
        await new Promise((resolve, reject) => {
          this.dispatchEvent(new CustomEvent('gift:select', { bubbles: true, detail: {
            kind: this.dataset.kind,
            variant: controlNode.dataset.variantId || controlNode.value,
            action: controlNode.dataset.sampleAction,
            source: this.dataset.source,
            resolve,
            reject
          } }));
        });
        if (announcementNode) {
          announcementNode.textContent = this.dataset.successText;
        }
      } catch (error) {
        statusNode.textContent = this.dataset.errorText;
        statusNode.focus();
      } finally {
        this.isBusy = false;
        this.removeAttribute('aria-busy');
        if (loaderNode) {
          loaderNode.hidden = true;
          optionNode.removeAttribute('aria-busy');
        }
        this.querySelectorAll('[data-gift-choice]').forEach((choiceNode) => { choiceNode.disabled = choiceNode.dataset.isUnavailable === 'true'; });
      }
    }

    updateSelection(cart) {
      const giftLines = cart.items.filter((lineItem) => lineItem.properties?._gift_selection === this.dataset.kind);
      if (this.dataset.kind === 'sample') {
        const quantity = giftLines.reduce((total, lineItem) => total + (String(lineItem.variant_id) === this.dataset.variantId ? lineItem.quantity : 0), 0);
        const totalQuantity = giftLines.reduce((total, lineItem) => total + lineItem.quantity, 0);
        this.dataset.quantity = String(quantity);
        this.closest('[data-gift-card]')?.classList.toggle('selected', quantity > 0);
        this.querySelector('[data-sample-add]').hidden = quantity > 0;
        this.querySelector('[data-sample-quantity-selector]').hidden = quantity === 0;
        this.querySelector('[data-sample-quantity]').value = String(quantity);
        const formQuantityNode = this.querySelector('[data-sample-form-quantity]');
        if (formQuantityNode) {
          formQuantityNode.value = String(quantity);
        }
        this.querySelectorAll('[data-gift-choice]').forEach((choiceNode) => {
          const isUnavailable = choiceNode.dataset.sampleAction === 'increase' ? totalQuantity >= 2 || quantity >= Number(this.dataset.maxQuantity) : quantity === 0;
          choiceNode.dataset.isUnavailable = String(isUnavailable);
          choiceNode.disabled = this.isBusy || isUnavailable;
        });
      } else {
        this.querySelectorAll('[data-gift-choice]').forEach((choiceNode) => {
          choiceNode.checked = giftLines.some((lineItem) => String(lineItem.variant_id) === choiceNode.value);
          choiceNode.disabled = this.isBusy;
        });
      }
    }
  }

  customElements.define('gift-selection', GiftSelection);
}

if (!customElements.get('sample-count')) {
  class SampleCount extends HTMLElement {
    connectedCallback() {
      this.abortController = new AbortController();
      document.documentElement.addEventListener('gift:updated', (updatedEvent) => {
        const quantity = updatedEvent.detail.cart.items.reduce((total, lineItem) => total + (lineItem.properties?._gift_selection === 'sample' ? lineItem.quantity : 0), 0);
        const message = this.dataset.message.replace('[count]', String(quantity));
        if (this.textContent !== message) {
          this.textContent = message;
        }
      }, { signal: this.abortController.signal });
    }

    disconnectedCallback() {
      this.abortController.abort();
    }
  }

  customElements.define('sample-count', SampleCount);
}
